import { useState } from "react";
import { toast } from "sonner";
import { Stack, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { EmptyState } from "@/promotions/components/EmptyState";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { ConfirmDialog } from "@/promotions/components/campaign/ConfirmDialog";
import { apiError } from "@/lib/api";
import { Modal, Field, TextInput, TextArea } from "../components/Modal";
import { useCreativeData, creativeApi } from "../useCreative";
import { ASPECT_RATIOS, TEMPLATE_USE_CASES } from "../creativeUtils";

const EMPTY = { name: "", use_case: "", target_platform: "", aspect_ratio: "1:1", family: "creative.image", kind: "image", description: "" };

export default function Templates() {
  const { templates, loading, reload } = useCreativeData();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const openCreate = () => setEditing({ ...EMPTY });
  const openEdit = (t) => setEditing({
    id: t.id, name: t.name || "", use_case: t.use_case || "", target_platform: t.target_platform || "",
    aspect_ratio: t.aspect_ratio || "1:1", family: t.family || "creative.image", kind: t.kind || "image", description: t.description || "",
  });

  const applyUseCase = (uc) => {
    const meta = TEMPLATE_USE_CASES.find((u) => u.value === uc);
    setEditing((s) => ({
      ...s,
      use_case: uc,
      target_platform: meta?.platform || s.target_platform,
      aspect_ratio: meta?.aspect || s.aspect_ratio,
      name: s.name || meta?.label || "",
    }));
  };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    setSaving(true);
    const body = {
      name: editing.name.trim(),
      use_case: editing.use_case || null,
      target_platform: editing.target_platform || null,
      aspect_ratio: editing.aspect_ratio || null,
      family: editing.family,
      kind: editing.kind,
      description: editing.description.trim() || null,
    };
    try {
      if (editing.id) await creativeApi.updateTemplate(editing.id, body);
      else await creativeApi.createTemplate(body);
      toast.success(editing.id ? "Template updated" : "Template created");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await creativeApi.deleteTemplate(toDelete.id);
      toast.success("Template deleted");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="creative-templates">
      <PromoPageHeader
        overline="Creative Studio"
        title="Templates"
        description="Preset marketing surfaces that map a use-case to output kind and geometry."
        actions={
          <button onClick={openCreate} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90" data-testid="template-new">
            <Plus size={16} weight="bold" /> New template
          </button>
        }
      />

      {loading ? (
        <CardsSkeleton count={6} />
      ) : templates.length === 0 ? (
        <EmptyState
          Icon={Stack}
          title="No templates yet"
          description="Create a template for a channel like Instagram Product or YouTube Thumbnail."
          action={<button onClick={openCreate} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">New template</button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="templates-grid">
          {templates.map((t) => (
            <div key={t.id} className="flex flex-col rounded-lg border border-border bg-card p-5" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-accent/40 text-muted-foreground">
                  <Stack size={18} weight="duotone" />
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(t)} aria-label="Edit template" className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid={`template-edit-${t.id}`}>
                    <PencilSimple size={15} />
                  </button>
                  <button onClick={() => setToDelete(t)} aria-label="Delete template" className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" data-testid={`template-delete-${t.id}`}>
                    <Trash size={15} />
                  </button>
                </div>
              </div>
              <h3 className="mt-3 font-medium tracking-tight">{t.name}</h3>
              {t.description && <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {t.target_platform && <Tag>{t.target_platform}</Tag>}
                {t.aspect_ratio && <Tag>{t.aspect_ratio}</Tag>}
                <Tag>{t.kind}</Tag>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit template" : "New template"}
        size="lg"
        footer={
          <>
            <button onClick={() => setEditing(null)} className="rounded-md border border-border px-3.5 py-2 text-sm font-medium transition-colors hover:bg-accent" data-testid="template-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="template-save">{saving ? "Saving…" : "Save"}</button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <Field label="Use case" hint="Selecting a use-case pre-fills platform & aspect ratio.">
              <PromoSelect
                value={editing.use_case}
                onChange={applyUseCase}
                placeholder="Custom"
                options={[{ value: "", label: "Custom" }, ...TEMPLATE_USE_CASES.map((u) => ({ value: u.value, label: u.label }))]}
                testId="template-select-use-case"
              />
            </Field>
            <Field label="Name">
              <TextInput value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="Instagram Product Shot" data-testid="template-input-name" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Kind">
                <PromoSelect value={editing.kind} onChange={(v) => setEditing((s) => ({ ...s, kind: v, family: `creative.${v}` }))} options={[{ value: "image", label: "Image" }, { value: "video", label: "Video" }]} testId="template-select-kind" />
              </Field>
              <Field label="Platform">
                <TextInput value={editing.target_platform} onChange={(e) => setEditing((s) => ({ ...s, target_platform: e.target.value }))} placeholder="instagram" data-testid="template-input-platform" />
              </Field>
              <Field label="Aspect ratio">
                <PromoSelect value={editing.aspect_ratio} onChange={(v) => setEditing((s) => ({ ...s, aspect_ratio: v }))} options={ASPECT_RATIOS.map((r) => ({ value: r, label: r }))} testId="template-select-aspect" />
              </Field>
            </div>
            <Field label="Description">
              <TextArea rows={2} value={editing.description} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} placeholder="Square product hero for the Instagram feed." data-testid="template-input-description" />
            </Field>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        danger
        title="Delete template?"
        description={toDelete ? `"${toDelete.name}" will be permanently removed.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}

function Tag({ children }) {
  return <span className="rounded-full border border-border bg-accent/40 px-2 py-0.5 text-xs capitalize text-muted-foreground">{children}</span>;
}
